"use client";

import React from "react";
import { CircleCheck, Loader, Cpu, Globe, Zap, Shield } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const services = [{
  title: "IT Consulting",
  description: "Strategic implementation of modern IT consulting solutions.",
  icon: Cpu
}, {
  title: "Cloud Migration",
  description: "Seamless transition to scalable cloud infrastructures.",
  icon: Globe
}, {
  title: "Digital Transformation",
  description: "Modernizing legacy systems for the future.",
  icon: Zap
}, {
  title: "Security & Compliance",
  description: "Robust protection for your enterprise data.",
  icon: Shield
}];
function ConsultForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-card/50 border border-border rounded-xl animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">
          Request Received
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-secondary/30 border-border/50 focus-visible:border-primary focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-secondary/30 border-border/50 focus-visible:border-primary focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+254 712 345 678" {...field} className="bg-secondary/30 border-border/50 focus-visible:border-primary focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">
                Company (Optional)
              </FormLabel>
              <FormControl>
                <Input placeholder="Inara Technologies Ltd." {...field} className="bg-secondary/30 border-border/50 focus-visible:border-primary focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_contact_time" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">
              Preferred Contact Time
            </FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className={cn("flex h-9 w-full rounded-md border border-border/50 bg-secondary/30 px-3 py-1 text-sm shadow-xs transition-colors", "file:border-0 file:bg-transparent file:text-sm file:font-medium", "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20 focus-visible:border-primary", "disabled:cursor-not-allowed disabled:opacity-50 text-foreground appearance-none")}>
                  <option value="No Preference">No Preference</option>
                  <option value="Morning (8am-12pm)">Morning (8am-12pm)</option>
                  <option value="Afternoon (12pm-4pm)">
                    Afternoon (12pm-4pm)
                  </option>
                  <option value="Evening (4pm-8pm)">Evening (4pm-8pm)</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "Please describe your project"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">
              Brief Description of Your Cloud Project
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your current challenges, goals, and any specific technologies you are interested in." className="min-h-[120px] bg-secondary/30 border-border/50 focus-visible:border-primary focus-visible:ring-primary/20 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Processing Request...
          </> : "Send Request"}
      </Button>
    </Form>;
}
export default function ConsultACloudExpert() {
  return <section id="consult-a-cloud-expert" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {}
          <div className="space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Expert Consultation
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
                Let's Build the <span className="text-primary">Future</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Partner with Inara Technologies to navigate the complexities of
                digital transformation. Our experts are ready to help you
                optimize infrastructure, implement intelligent automation, and
                scale with confidence.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {services.map((service, i) => <div key={i} data-index={i} className="flex items-start space-x-4 p-4 rounded-xl bg-card/30 border border-border/50 hover:border-primary/30 transition-colors duration-300">
                  <div className="flex-shrink-0 p-2 rounded-lg bg-primary/10 text-primary">
                    <service.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {service.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6 border-t border-border/50">
              <p className="text-sm text-muted-foreground mb-4">
                Trusted by innovative companies across Kenya
              </p>
              <div className="flex flex-wrap gap-6 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                {}
                <span className="text-xl font-bold text-foreground/40">
                  TechStart
                </span>
                <span className="text-xl font-bold text-foreground/40">
                  GreenEnergy
                </span>
                <span className="text-xl font-bold text-foreground/40">
                  FinServe
                </span>
                <span className="text-xl font-bold text-foreground/40">
                  AgriTech
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-blue-600/20 rounded-2xl blur-lg opacity-75" />
            <div className="relative bg-card border border-border/50 rounded-xl p-6 md:p-8 shadow-2xl">
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-2">
                  Consult a Cloud Expert
                </h3>
                <p className="text-muted-foreground">
                  Fill out the form below and our team will get back to you
                  within 24 hours.
                </p>
              </div>

              <FormProvider formId="consult_cloud_expert_solutions" sectionName="consult-expert-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              project_description: "",
              preferred_contact_time: "No Preference"
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              project_description: {
                required: true
              }
            }} submitText="Send Request" successMessage="Thank you! Your consultation request has been received. We'll be in touch shortly.">
                <ConsultForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}